import { useTranslation } from 'react-i18next';
import ServiceLayout from './components/ServiceLayout';
import useSeoPage from '@/hooks/useSeoPage';

export default function ImplantationPage() {
  const { t } = useTranslation();
  const siteUrl = import.meta.env.VITE_SITE_URL ?? '';

  useSeoPage({
    title: 'Имплантация зубов в Кишинёве | Зубные импланты | Zimbet32',
    description: 'Установка зубных имплантов в Кишинёве. All-on-4, одиночная и множественная имплантация, синус-лифтинг. Zimbet32 — implantologie dentară modernă în Chișinău.',
    keywords: 'имплантация зубов Кишинёв, зубные импланты, All-on-4, имплантат зуба, implant dentar Chișinău, implantologie dentară, Zimbet32',
    canonicalPath: '/services/implantation',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'MedicalProcedure',
      name: 'Имплантация зубов',
      url: `${siteUrl}/services/implantation`,
      description: 'Имплантация зубов в Кишинёве с использованием качественных имплантатов. All-on-4, одиночная и множественная имплантация.',
      procedureType: 'https://schema.org/SurgicalProcedure',
      bodyLocation: 'Зубы, костная ткань челюсти',
      provider: {
        '@type': 'Dentist',
        name: 'Zimbet32',
        url: siteUrl,
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Strada București 60',
          addressLocality: 'Chișinău',
          postalCode: 'MD-2012',
          addressCountry: 'MD',
        },
        telephone: '+373-22-22-07-09',
      },
    },
  });

  const procedures = [
    {
      icon: 'ri-tooth-line',
      title: t('servicePages.implantation.procedures.single.title'),
      description: t('servicePages.implantation.procedures.single.description')
    },
    {
      icon: 'ri-hospital-line',
      title: t('servicePages.implantation.procedures.multiple.title'),
      description: t('servicePages.implantation.procedures.multiple.description')
    },
    {
      icon: 'ri-heart-pulse-line',
      title: t('servicePages.implantation.procedures.allOn4.title'),
      description: t('servicePages.implantation.procedures.allOn4.description')
    },
    {
      icon: 'ri-microscope-line',
      title: t('servicePages.implantation.procedures.bonePlasty.title'),
      description: t('servicePages.implantation.procedures.bonePlasty.description')
    },
    {
      icon: 'ri-shield-check-line',
      title: t('servicePages.implantation.procedures.sinusLift.title'),
      description: t('servicePages.implantation.procedures.sinusLift.description')
    },
    {
      icon: 'ri-time-line',
      title: t('servicePages.implantation.procedures.immediate.title'),
      description: t('servicePages.implantation.procedures.immediate.description')
    }
  ];

  const advantages = [
    {
      icon: 'ri-checkbox-circle-line',
      title: t('servicePages.implantation.benefits.permanent.title'),
      description: t('servicePages.implantation.benefits.permanent.description')
    },
    {
      icon: 'ri-heart-line',
      title: t('servicePages.implantation.benefits.natural.title'),
      description: t('servicePages.implantation.benefits.natural.description')
    },
    {
      icon: 'ri-shield-star-line',
      title: t('servicePages.implantation.benefits.boneSaving.title'),
      description: t('servicePages.implantation.benefits.boneSaving.description')
    }
  ];

  const galleryImages = [
    'https://public.readdy.ai/ai/img_res/b6838dc5f9dee68864dc131fddb0a197.jpg',
    'https://readdy.ai/api/search-image?query=dental%20implant%20crown%20installation%20process%20showing%20ceramic%20crown%20being%20attached%20to%20titanium%20abutment%20in%20bright%20modern%20dental%20office%20with%20professional%20equipment%20and%20clean%20white%20background%20highlighting%20precision%20work&width=800&height=600&seq=implant-crown-2&orientation=landscape',
    'https://readdy.ai/api/search-image?query=all-on-4%20dental%20implant%20full%20arch%20restoration%20showing%20complete%20set%20of%20beautiful%20white%20teeth%20on%20implant%20supported%20bridge%20in%20modern%20dental%20clinic%20with%20professional%20lighting%20and%20clean%20medical%20environment&width=800&height=600&seq=implant-allon4-3&orientation=landscape',
    'https://readdy.ai/api/search-image?query=dental%20implant%20x-ray%20radiograph%20showing%20titanium%20implant%20integrated%20in%20jawbone%20with%20perfect%20osseointegration%20displayed%20on%20modern%20digital%20screen%20in%20professional%20dental%20clinic%20with%20clean%20white%20background&width=800&height=600&seq=implant-xray-4&orientation=landscape',
    'https://readdy.ai/api/search-image?query=bone%20grafting%20procedure%20for%20dental%20implant%20preparation%20showing%20surgical%20site%20with%20bone%20graft%20material%20in%20modern%20dental%20clinic%20with%20sterile%20instruments%20and%20professional%20medical%20lighting%20in%20clean%20environment&width=800&height=600&seq=implant-bone-5&orientation=landscape',
    'https://readdy.ai/api/search-image?query=beautiful%20smile%20after%20dental%20implant%20treatment%20showing%20natural%20looking%20white%20teeth%20and%20healthy%20gums%20in%20bright%20modern%20dental%20office%20with%20professional%20photography%20lighting%20and%20clean%20white%20background&width=800&height=600&seq=implant-result-6&orientation=landscape'
  ];

  return (
    <ServiceLayout
      heroImage="https://readdy.ai/api/search-image?query=modern%20dental%20implant%20surgery%20room%20with%20advanced%20equipment%20titanium%20implants%20and%20professional%20dental%20team%20in%20sterile%20white%20medical%20environment%20with%20bright%20lighting%20and%20clean%20organized%20workspace%20showing%20precision%20instruments&width=1920&height=600&seq=implant-hero&orientation=landscape"
      breadcrumbKey="servicePages.implantation.title"
      titleKey="servicePages.implantation.title"
      descriptionKey="servicePages.implantation.description"
      procedures={procedures}
      advantages={advantages}
      galleryImages={galleryImages}
      ctaTitleKey="servicePages.implantation.ctaTitle"
      ctaDescriptionKey="servicePages.implantation.ctaDescription"
      ctaButtonKey="servicePages.implantation.ctaButton"
    />
  );
}
